import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCopy, FiCheck } = FiIcons;

const starters = [
  {
    id: 'couples',
    title: 'For Couples',
    template: 'We celebrated our wedding at Padua and what stood out most was __________. The venue felt __________, and our favorite memory was __________. It was a beautiful setting for such an important day, and we’re grateful for the experience.'
  },
  {
    id: 'guests',
    title: 'For Wedding Guests',
    template: 'I attended a wedding at Padua and the venue made the celebration feel __________. My favorite part was __________, and the atmosphere felt __________. It was a memorable place to celebrate with family and friends.'
  },
  {
    id: 'families',
    title: 'For Family Members',
    template: 'Our family celebrated a special wedding at Padua and the experience felt __________. One of the most memorable parts was __________. It was a beautiful setting for such an important moment for our family.'
  },
  {
    id: 'vendors',
    title: 'For Vendors',
    template: 'As a vendor, working at Padua was a smooth and professional experience. The venue was __________, the setting was __________, and the event atmosphere felt __________. It’s a beautiful venue for weddings and special events.'
  }
];

const ReviewHelper = ({ onCopy }) => {
  const [copiedId, setCopiedId] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    if (onCopy) onCopy();
    setTimeout(() => setCopiedId(null), 3000);
  };

  return (
    <section className="w-full">
      <div className="text-center mb-10">
        <h3 className="font-serif text-3xl md:text-4xl text-padua-dark mb-4">Need Help Finding the Words?</h3>
        <p className="text-padua-sage font-medium max-w-lg mx-auto leading-relaxed mb-2">
          Choose a starter, personalize it in your own voice, then share what made your Padua experience memorable.
        </p>
        <p className="text-xs text-padua-gold/80 italic font-medium">
          “These starters are only a guide — your honest words mean the most.”
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {starters.map((starter) => (
          <motion.div
            key={starter.id}
            whileHover={{ y: -4 }}
            className="bg-white border border-padua-sage/20 rounded-2xl p-6 md:p-8 shadow-sm flex flex-col h-full"
          >
            <h4 className="font-serif text-xl text-padua-olive mb-4">{starter.title}</h4>
            <p className="text-sm md:text-base text-gray-600 font-light italic leading-relaxed mb-6 flex-grow">
              "{starter.template}"
            </p>
            <button
              onClick={() => copyToClipboard(starter.template, starter.id)}
              className={`flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 ${
                copiedId === starter.id 
                  ? 'bg-padua-gold text-white' 
                  : 'bg-padua-lightSage text-padua-olive hover:bg-padua-sage hover:text-white'
              }`}
            >
              <SafeIcon icon={copiedId === starter.id ? FiCheck : FiCopy} className="w-4 h-4" />
              {copiedId === starter.id ? 'Copied to Clipboard' : `Copy ${starter.id.charAt(0).toUpperCase() + starter.id.slice(1, -1)} Starter`}
            </button>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {copiedId && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-padua-dark text-padua-ivory px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 border border-padua-gold/30"
          >
            <div className="bg-padua-gold rounded-full p-1">
              <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-medium tracking-wide">
              Copied — personalize it in your own voice, then share your experience.
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ReviewHelper;